import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Users, Trash2, Loader2 } from 'lucide-react';
import { documentSharingApi } from '../services/documentSharingApi';
import { DocumentParticipant, DocumentRole, DocumentParticipantsResponse } from '../types/documentSharing';
import { ConfirmationDialog } from './ConfirmationDialog';
import { KnowledgeItem } from '../services/api';

interface DocumentParticipantsModalProps {
  isOpen: boolean;
  onClose: () => void;
  knowledgeItemId: string;
  documentTitle: string;
  knowledgeItem?: KnowledgeItem; // Добавляем для использования кешированных данных
}

export const DocumentParticipantsModal: React.FC<DocumentParticipantsModalProps> = ({
  isOpen,
  onClose,
  knowledgeItemId,
  documentTitle,
  knowledgeItem
}) => {
  const [participants, setParticipants] = useState<DocumentParticipant[]>([]);
  const [isAuthor, setIsAuthor] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [participantToRemove, setParticipantToRemove] = useState<{ userId: string; username: string } | null>(null);

  // Загрузка участников при открытии модала
  useEffect(() => {
    if (isOpen) {
      // Если есть кешированные данные об участниках, используем их
      if (knowledgeItem && knowledgeItem.isShared !== undefined && knowledgeItem.isAuthor !== undefined) {
        console.log('[DocumentParticipantsModal] Используем кешированные данные об участниках');
        
        // Если документ не расшарен
        if (knowledgeItem.isShared === false) {
          setParticipants([]);
          setIsAuthor(true);
          setIsLoading(false);
          setError(null);
          return;
        }
        
        // Если документ расшарен и есть информация об участниках
        if (knowledgeItem.participants && Array.isArray(knowledgeItem.participants)) {
          setParticipants(knowledgeItem.participants.map(p => ({
            id: p.id,
            knowledgeItemId: knowledgeItemId,
            userId: p.userId,
            username: p.username,
            fullName: p.fullName,
            role: p.role as DocumentRole,
            createdAt: p.createdAt
          })));
          setIsAuthor(knowledgeItem.isAuthor);
          setIsLoading(false);
          setError(null);
          return;
        }
        
        // Если есть базовая информация, но нет детального списка участников
        if (knowledgeItem.participantCount !== undefined) {
          setParticipants([]);
          setIsAuthor(knowledgeItem.isAuthor);
          setIsLoading(false);
          setError(null);
          return;
        }
      }
      
      // Если кешированных данных нет, загружаем с сервера (fallback)
      console.log('[DocumentParticipantsModal] Fallback: загружаем данные с сервера');
      loadParticipants();
    }
  }, [isOpen, knowledgeItemId, knowledgeItem]);

  const loadParticipants = async () => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('[DocumentParticipantsModal] Загружаем данные с сервера (нет кеша)');
      const response: DocumentParticipantsResponse = await documentSharingApi.getParticipants(knowledgeItemId);
      setParticipants(response.participants || []);
      setIsAuthor(response.isAuthor || false);
    } catch (err: any) {
      console.error('Ошибка при загрузке участников:', err);
      setError('Не удалось загрузить список участников');
      setParticipants([]); // Устанавливаем пустой массив при ошибке
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveParticipant = (userId: string, username: string) => {
    if (!isAuthor) return;
    
    setParticipantToRemove({ userId, username });
    setShowConfirmDialog(true);
  };

  const confirmRemoveParticipant = async () => {
    if (!participantToRemove) return;
    
    const { userId } = participantToRemove;
    setIsRemoving(userId);
    try {
      await documentSharingApi.removeParticipant(knowledgeItemId, userId);
      setParticipants(prev => prev.filter(p => p.userId !== userId));
    } catch (err: any) {
      console.error('Ошибка при удалении участника:', err);
      setError('Не удалось удалить участника');
    } finally {
      setIsRemoving(null);
      setParticipantToRemove(null);
    }
  };

  const getRoleText = (role: DocumentRole) => {
    return role === DocumentRole.Author ? 'Автор' : 'Редактор';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Затемненный фон */}
      <motion.div
        className="absolute inset-0 bg-black/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      />
      
      {/* Модальное окно */}
      <motion.div
        className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg"
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.2 }}
      >
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <Users className="w-6 h-6 text-blue-500" />
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Участники документа
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {documentTitle}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Содержимое */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-600 dark:text-gray-400">Загрузка участников...</span>
            </div>
          ) : (
            <div className="space-y-3">
              {!participants || participants.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">Нет участников</p>
                </div>
              ) : (
                participants.map((participant) => (
                  <div
                    key={participant.id}
                    className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          participant.role === DocumentRole.Author 
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                        }`}>
                          {getRoleText(participant.role)}
                        </span>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {participant.fullName || participant.username}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mt-1">
                        <span>@{participant.username}</span>
                        <span>•</span>
                        <span className="flex items-right">{formatDate(participant.createdAt)}</span>
                      </div>
                    </div>

                    {/* Кнопка удаления (только для автора и не для самого автора) */}
                    {isAuthor && participant.role !== DocumentRole.Author && (
                      <button
                        onClick={() => handleRemoveParticipant(participant.userId, participant.username)}
                        disabled={isRemoving === participant.userId}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Удалить участника"
                      >
                        {isRemoving === participant.userId ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Кнопка закрытия */}
          <div className="flex justify-end mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors font-medium"
            >
              Закрыть
            </button>
          </div>
        </div>
      </motion.div>

      {/* Диалог подтверждения удаления */}
      <ConfirmationDialog
        isOpen={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setParticipantToRemove(null);
        }}
        onConfirm={confirmRemoveParticipant}
        title="Удалить участника"
        message={`Вы уверены, что хотите удалить участника @${participantToRemove?.username}? Это действие нельзя отменить.`}
        confirmText="Удалить"
        cancelText="Отмена"
        type="danger"
      />
    </div>
  );
}; 